"use client";
import { TooltipProvider } from "@cmp-src/primitives/tooltip";
import {
  AuthenticatedUser,
  PossibleDateSort,
  TableOverviewUser,
  TacticApiClient
} from "@gettactic/api";
import { InfiniteScroll } from "@gettactic/components/src/components/InfiniteScroll/InfiniteScroll";
import { OfficeStatusCell } from "@gettactic/components/src/components/WeeklyOffice/cells/OfficeStatusCell";
import { UserCell } from "@gettactic/components/src/components/WeeklyOffice/cells/UserCell";
import { getWorkingLocationStatus } from "@gettactic/components/src/components/WeeklyOffice/cells/WorkingLocationPresenceIcon";
import { NameColumnFilter } from "@gettactic/components/src/components/WeeklyOffice/controls/NameColumnFilter";
import { SortButton } from "@gettactic/components/src/components/WeeklyOffice/controls/SortButton";
import { WeeklyDataGridWrapper } from "@gettactic/components/src/components/WeeklyOffice/WeeklyDataLayout";
import { makeWeeklyTableId } from "@gettactic/components/src/components/WeeklyOffice/weekly-office-helpers";
import { useColumnWidth } from "@gettactic/components/src/contexts/ColumnWidthContext";
import { cn, limitWeekDateStrings } from "@gettactic/helpers";
import { dateStringToLocalTime } from "@gettactic/helpers/src/date-time/dateStringToLocalTime";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { format, parse } from "date-fns";
import { ChevronUpIcon } from "lucide-react";
import React, { useMemo } from "react";
import {
  CellProps,
  Column,
  ColumnInstance,
  Row,
  useFilters,
  useGlobalFilter,
  useSortBy,
  useTable
} from "react-table";
import { useDashboardFilters } from "@/features/dashboard/DashboardStateContext";
import { AuthenticatedUserRow } from "./AuthenticatedUserRow";
import { WeeklyTableSkeleton } from "./WeeklyTableSkeleton";

const UserSearchContainer = React.memo(
  ({ children }: { children: React.ReactNode }) => (
    <div className="relative">{children}</div>
  )
);

export const WeeklyTable = ({
  data,
  formattedWeek,
  authenticatedUser,
  userSearch,
  favsApi,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  isLoading,
  daysCount,
  showAsMobileDaily,
  selectedDayIndex
}: {
  data: TableOverviewUser[];
  formattedWeek: string[];
  authenticatedUser: AuthenticatedUser;
  userSearch?: React.ReactNode;
  favsApi: TacticApiClient;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  daysCount: number;
  showAsMobileDaily?: boolean;
  selectedDayIndex?: number;
}) => {
  const { sortByDate, filters } = useDashboardFilters();
  const { dateToSortBy, dateSortOrder } = filters;
  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");
  const arrowDirection = useMemo(
    () =>
      dateSortOrder === PossibleDateSort.ASC
        ? PossibleDateSort.DESC
        : PossibleDateSort.ASC,
    [dateSortOrder]
  );
  const handleDayHeaderClick = (date: string, _index: number) => {
    sortByDate(date);
  };

  // Icons must be compared against the offices the rows were fetched for,
  // not the viewer's own assigned office
  const filterOfficeIds = useMemo(
    () =>
      filters.offices.length > 0
        ? filters.offices
        : authenticatedUser.officeId
          ? [authenticatedUser.officeId]
          : null,
    [filters.offices, authenticatedUser.officeId]
  );

  const { nameColumnWidth } = useColumnWidth();
  const offices = authenticatedUser.offices?.byId ?? {};
  // Memoize headers and limitedFormattedWeek
  const limitedFormattedWeek = React.useMemo(() => {
    return limitWeekDateStrings(formattedWeek, daysCount).filter(
      (date) =>
        !showAsMobileDaily || date === formattedWeek[selectedDayIndex || 0]
    );
  }, [formattedWeek, daysCount, selectedDayIndex, showAsMobileDaily]);

  // Single-letter day labels derived from the actual dates so they stay
  // aligned with the configurable week start day
  const headers = React.useMemo(
    () =>
      limitedFormattedWeek.map((date) =>
        format(parse(date, "yyyy-MM-dd", new Date()), "EEEEE")
      ),
    [limitedFormattedWeek]
  );

  // Index within the rendered (weekday-limited) columns, not the full week
  const arrowIndex: number | undefined = useMemo(
    () => limitedFormattedWeek.indexOf(dateToSortBy ?? ""),
    [limitedFormattedWeek, dateToSortBy]
  );

  const currentUserId = authenticatedUser.user?.id ?? "";

  const columns = React.useMemo<Column<TableOverviewUser>[]>(
    () => [
      {
        Header: "Name",
        accessor: (row: TableOverviewUser) => row.user.name,
        Control: (props: { column: ColumnInstance<TableOverviewUser> }) => (
          <NameColumnFilter
            {...props}
            UserSearch={<UserSearchContainer>{userSearch}</UserSearchContainer>}
          />
        ),
        filter: "text",
        value: ({ row }: { row: Row<TableOverviewUser> }) =>
          row.original.user.name,
        Cell: (props: CellProps<TableOverviewUser>) => {
          return (
            <UserCell
              {...props}
              row={{
                ...props?.row,
                original: {
                  user: props.row.original.user
                }
              }}
              favsApi={favsApi}
              isMe={props.row.original.user.id === currentUserId}
              hasAnyFav={props.row.original.favorite_team_ids.length > 0}
            />
          );
        }
      },
      ...limitedFormattedWeek.map((col, index) => {
        const key = makeWeeklyTableId(headers, col, index);
        return {
          Header: headers[index],
          Control: (props: { column: ColumnInstance<TableOverviewUser> }) => (
            <SortButton column={props.column} />
          ),
          id: key,
          accessor: (row: TableOverviewUser) => {
            const status = getWorkingLocationStatus({
              assignedPolicyType: row.working_locations?.[col]?.policy?.type,
              filterOfficeId: filterOfficeIds,
              officeIdOrOtherLocation: authenticatedUser.officeId ?? "",
              locationIsAssigned: !!row.working_locations?.[col]?.location,
              assignedLocation: row.working_locations?.[col]?.location ?? "",
              assignedOfficeId: row.working_locations?.[col]?.office_id ?? null,
              assignedResource:
                row.working_locations?.[col]?.schedules[0]?.resource ?? null
            });
            return status;
          },
          Cell: ({ row, value }: CellProps<TableOverviewUser>) => {
            const officeId = row.original?.working_locations?.[col]?.office_id;
            const office = officeId ? offices[officeId] : null;

            const startTime =
              row.original?.working_locations?.[col]?.schedules[0]?.start;
            const endTime =
              row.original?.working_locations?.[col]?.schedules[0]?.end;

            return (
              <div className="w-full min-w-10 md:min-w-auto">
                <OfficeStatusCell
                  officeName={office?.name}
                  officeId={office?.id}
                  status={value}
                  resource={
                    row.original?.working_locations?.[col]?.schedules[0]
                      ?.resource
                  }
                  date={col}
                  startTime={
                    startTime ? dateStringToLocalTime(startTime) : undefined
                  }
                  endTime={endTime ? dateStringToLocalTime(endTime) : undefined}
                />
              </div>
            );
          },
          sort: "text"
          // sortingFn: (rowA, rowB) => (StatusPriority[rowA.value] > StatusPriority[rowB.value])
        };
      })
    ],
    [
      data,
      limitedFormattedWeek,
      headers,
      filterOfficeIds,
      favsApi,
      currentUserId,
      authenticatedUser.officeId
    ]
  );

  const tableInstance = useTable(
    {
      columns,
      data
    },
    useFilters,
    useGlobalFilter,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  const scrollRef = React.useRef<HTMLDivElement>(null);

  // Calculate column count based on daysCount plus the name column
  const columnCount = daysCount + 1;

  // Cell styling for name column vs day columns
  const getNameCellStyle = () => ({
    width: "100%",
    height: "100%",
    margin: 0,
    display: "flex",
    alignItems: "center"
  });

  const getDayCellStyle = () => ({
    width: "100%",
    height: "100%",
    margin: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  });

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex flex-col">
        {!showAsMobileDaily && (
          <div className="sticky top-0 z-50 bg-background">
            <WeeklyDataGridWrapper
              nameColumnWidth={nameColumnWidth}
              daysCount={daysCount}
              className={"flex-row md:flex-col"}
            >
              {headerGroups[0].headers.map((column, columnIndex) => {
                return (
                  <div
                    {...column.getHeaderProps()}
                    key={column.id}
                    className="relative h-full w-full py-2 text-neutral-700"
                    style={
                      columnIndex === 0 ? getNameCellStyle() : getDayCellStyle()
                    }
                    onClick={() => {
                      // Only handle clicks for day columns (skip name column)
                      if (columnIndex > 0) {
                        handleDayHeaderClick(
                          limitedFormattedWeek[columnIndex - 1],
                          columnIndex - 1
                        );
                      }
                    }}
                  >
                    {column.Control ? (
                      <div className="w-full">{column.render("Control")}</div>
                    ) : (
                      <div>{column.render("Header")}</div>
                    )}
                    <ArrowIcon
                      direction={
                        columnIndex - 1 === arrowIndex && arrowIndex !== -1
                          ? arrowDirection
                          : undefined
                      }
                    />
                  </div>
                );
              })}
            </WeeklyDataGridWrapper>
          </div>
        )}
        <WeeklyDataGridWrapper
          nameColumnWidth={nameColumnWidth}
          daysCount={daysCount}
        >
          <div
            {...getTableProps()}
            className="contents grid-rows-[auto]"
            ref={scrollRef}
          >
            <AuthenticatedUserRow
              authenticatedUser={authenticatedUser}
              headers={headers}
              favsApi={favsApi}
              columnCount={columnCount}
              showAsMobileDaily={showAsMobileDaily}
              selectedDayIndex={selectedDayIndex}
            />

            <div {...getTableBodyProps()} className="contents">
              {rows.map((row) => {
                prepareRow(row);

                const getCellContent = (
                  cell: (typeof row.cells)[0],
                  cellIndex: number
                ) => {
                  const { key, ...cellProps } = cell.getCellProps();
                  return (
                    <div
                      {...cellProps}
                      key={key}
                      className="overflow-hidden"
                      style={
                        cellIndex === 0 ? getNameCellStyle() : getDayCellStyle()
                      }
                    >
                      {cell.render("Cell")}
                    </div>
                  );
                };

                const userColumn = !isMd
                  ? getCellContent(row.cells[0], 0)
                  : undefined;

                const content = (
                  <>
                    {row.cells.map((cell, cellIndex) => {
                      if (!isMd && !cellIndex) return null;
                      return getCellContent(cell, cellIndex);
                    })}
                  </>
                );

                return (
                  <React.Fragment key={row.id}>
                    {isMd ? (
                      content
                    ) : (
                      <div
                        className={
                          "flex flex-shrink-0 border-b border-b-neutral-100"
                        }
                      >
                        {userColumn}
                        <div className={"flex flex-shrink-0 gap-1"}>
                          {content}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          {hasNextPage && (
            <InfiniteScroll
              hasNextPage={!!hasNextPage}
              fetchNextPage={fetchNextPage || (() => {})}
              isFetchingNextPage={!!isFetchingNextPage}
              disabled={!!isLoading || data.length === 0}
              offset={200}
              scrollRef={scrollRef}
              spinner={<WeeklyTableSkeleton columnCount={columnCount} />}
            />
          )}
        </WeeklyDataGridWrapper>
      </div>
    </TooltipProvider>
  );
};

const ArrowIcon = ({ direction }: { direction?: PossibleDateSort }) => {
  if (!direction) {
    return null;
  }
  return (
    <div className="-translate-y-1/2 absolute top-1/2 right-5">
      <ChevronUpIcon
        className={cn(
          "h-3.5 w-3.5 text-muted-foreground transition-transform duration-100",
          direction === PossibleDateSort.DESC ? "rotate-180" : ""
        )}
      />
    </div>
  );
};
